// apps/web/src/router.tsx
//
// Plan 04-12b — React Router 6 (data routers) with functional auth pages.
//
// Replaces Plan 12a's placeholder routes with the real form components.
// Two exports preserved from Plan 12a:
//   AppRoutes — the bare <Routes>/<Route> tree, suitable for embedding under
//               a MemoryRouter in tests.
//   AppRouter — production wrapper that uses createBrowserRouter +
//               RouterProvider (talks to window.history).
import { lazy, Suspense } from "react";
import { createBrowserRouter, Route, RouterProvider, Routes } from "react-router-dom";

import { RequireCanAuthor } from "./components/RequireCanAuthor";

// Plan 12a placeholders for the account routes that Plan 12b still has not
// fully populated at the moment this file is imported — but Task 3 below adds
// real pages for /account/security, /account/email, /account/redeem-invite.
import { EmailChange } from "./pages/account/EmailChange";
import { RedeemInvite } from "./pages/account/RedeemInvite";
import { Security } from "./pages/account/Security";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { LinkExisting } from "./pages/auth/LinkExisting";
import { MfaChallenge } from "./pages/auth/MfaChallenge";
import { MfaEnroll } from "./pages/auth/MfaEnroll";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { SignIn } from "./pages/auth/SignIn";
import { SignUp } from "./pages/auth/SignUp";
import { VerifyEmail } from "./pages/auth/VerifyEmail";
// Plan 08-10 — Author UI top-level page (paste-JSON, fixture-load, save flow).
import { RecipeEditor } from "./pages/author/RecipeEditor";
// Plan 09-11 — Spread Composer top-level page (SPRD-01).
// Plan 09-13 (this plan) mounts the routes per UI-SPEC §"Spread Composer shell
// — net-new this phase" lines 124-128: /spread/new + /spread/:spreadId both
// point at SpreadComposer.
import { SpreadComposer } from "./pages/spread/SpreadComposer";
// Landing page at "/" — lists the seeded recipes + spreads for the review deploy.
import { HomePage } from "./pages/home/HomePage";

// Plan 10-10 — AI Extract entry surface (Surface 1, AI-01 / AI-07). Lazy-loaded
// behind the RequireCanAuthor guard so the extraction bundle only loads for an
// authenticated authoring session. Mirrors the /recipes/new authoring-gate
// intent: authenticated + can_author (server-enforced; guard is client-side
// defense-in-depth — see RequireCanAuthor.tsx).
const ExtractForm = lazy(() => import("./pages/extract/ExtractForm"));

// Plan 10-11 — AI review checkpoint (Surface 2, AI-10). The mandatory human
// review gate between extraction and persist. Lazy-loaded behind RequireCanAuthor
// (client-side defense-in-depth); the server enforces the authoritative
// assertCanWrite ACL on GET/POST /api/recipes/:id/review (Plan 10-08). The route
// param is :id to match the review-route contract + useParams<{ id }>.
const RecipeReviewPage = lazy(() => import("./pages/review/RecipeReviewPage"));

// Plan 10-13 — Admin AI-model config page (Surface 4, AI-04). Lazy-loaded behind
// RequireCanAuthor (client-side authentication gate, defense-in-depth). The
// AUTHORITATIVE admin gate is server-side: GET/PUT /api/admin/ai-models return
// 403 admin_only for a non-admin (Plan 10-09 isAdmin), which the page surfaces
// as the "You don't have admin access." copy. Per 10-UI-SPEC line 914 the
// client route gate is defense-in-depth only; 10-13 ships the simpler
// always-render version rather than a client-side admin pre-check.
const AIModelAdminPage = lazy(() => import("./pages/admin/AIModelAdminPage"));

// Phase 11.2 Plan 04 — authed view routes mounting the extracted
// RecipeRenderEngine standalone. Lazy-loaded behind RequireCanAuthor
// (client-side defense-in-depth); the AUTHORITATIVE gate is the server GET
// (assertCanRead, 404-on-deny — enumeration defense). The route param is :id to
// match the review-route contract + useParams<{ id }>.
const RecipeViewPage = lazy(() => import("./pages/view/RecipeViewPage"));
const SpreadViewPage = lazy(() => import("./pages/view/SpreadViewPage"));

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/sign-up" element={<SignUp />} />
      <Route path="/auth/sign-in" element={<SignIn />} />
      <Route path="/auth/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password/:token" element={<ResetPassword />} />
      <Route path="/auth/mfa-challenge" element={<MfaChallenge />} />
      <Route path="/auth/mfa-enroll" element={<MfaEnroll />} />
      <Route path="/auth/link-existing" element={<LinkExisting />} />
      <Route path="/account/security" element={<Security />} />
      <Route path="/account/email" element={<EmailChange />} />
      <Route path="/account/redeem-invite" element={<RedeemInvite />} />
      <Route path="/author/recipe/new" element={<RecipeEditor />} />
      <Route path="/author/recipe/:recipeId" element={<RecipeEditor />} />
      <Route path="/spread/new" element={<SpreadComposer />} />
      <Route path="/spread/:spreadId" element={<SpreadComposer />} />
      <Route
        path="/recipes/extract"
        element={
          <RequireCanAuthor>
            <Suspense fallback={<div aria-busy="true">Loading…</div>}>
              <ExtractForm />
            </Suspense>
          </RequireCanAuthor>
        }
      />
      <Route
        path="/recipes/:id/review"
        element={
          <RequireCanAuthor>
            <Suspense fallback={<div aria-busy="true">Loading…</div>}>
              <RecipeReviewPage />
            </Suspense>
          </RequireCanAuthor>
        }
      />
      <Route
        path="/admin/ai-models"
        element={
          <RequireCanAuthor>
            <Suspense fallback={<div aria-busy="true">Loading…</div>}>
              <AIModelAdminPage />
            </Suspense>
          </RequireCanAuthor>
        }
      />
      <Route
        path="/recipes/:id/view"
        element={
          <RequireCanAuthor>
            <Suspense fallback={<div aria-busy="true">Loading…</div>}>
              <RecipeViewPage />
            </Suspense>
          </RequireCanAuthor>
        }
      />
      <Route
        path="/spreads/:id/view"
        element={
          <RequireCanAuthor>
            <Suspense fallback={<div aria-busy="true">Loading…</div>}>
              <SpreadViewPage />
            </Suspense>
          </RequireCanAuthor>
        }
      />
      <Route path="/" element={<HomePage />} />
    </Routes>
  );
}

const browserRouter = createBrowserRouter([
  {
    path: "*",
    element: <AppRoutes />,
  },
]);

export function AppRouter() {
  return <RouterProvider router={browserRouter} />;
}
